/* eslint-disable @typescript-eslint/no-unused-vars */
import axios, { AxiosError } from 'axios';
import { Environment } from '../../../environment';
import { v4 as uuidv4 } from 'uuid';
import { Api } from '../axiosApi';
import { traduzirErroFiscal, IItemParaMapeamentoErro } from '../../../utils/traduzirErroFiscal';

// itensnfe.gtin (coluna real, só isso é persistido) guarda o mesmo código de
// barras que os dialogs de item chamam de `ean` — sem esse mapeamento no
// envio, o valor digitado/selecionado na tela fica só no state e o item
// grava gtin nulo pra sempre (é o que a rota de venda->nfe faz certo, via
// p.ean, mas a emissão manual de NFe não tinha esse passo). "SEM GTIN" é o
// placeholder de produto sem código de barras cadastrado — vira null aqui,
// mesmo critério do validarGtin em validarNotaFiscal.ts.
const eanParaGtin = (ean: string | undefined | null): string | null => {
  const valor = (ean ?? '').trim();
  if (!valor || valor.toUpperCase() === 'SEM GTIN') return null;
  return valor;
};

export interface IListagemNfe {
  idnfe: string;
  dhemi: Date;
  nfe: number;
  produtor: string;
  parceiros: string;
  cpf: string;
  vnfe: number;
  status: number;
  finnfe: number;
  natureza: string;
  // Histórico de emissão (ver providers/historicoemissao no backend):
  // quantas tentativas deram erro e o último motivo/etapa.
  qtderros?: number;
  motivoerro?: string | null;
  etapaerro?: string | null;
}
export interface IListagemNfece {
  idnfce: string;
  dhemi: Date;
  nfe: number;
  vnfe: number;
  status: number;
  // Histórico de emissão (ver providers/historicoemissao no backend):
  // quantas tentativas deram erro e o último motivo/etapa.
  qtderros?: number;
  motivoerro?: string | null;
  etapaerro?: string | null;
}
export interface IitensNfe {
  iditens: string;
  idprodutos: string;
  vfrete: number;
  voutros: number;
  vicmsst: number;
  ean: string;
  descricao: string;
  ncm: string;
  origem: number;
  cfop: number | null;
  un: string;
  untrib: string;
  qtd: number;
  qtdtrib: number;
  preco: number;
  precotrib: number;
  cst: string;
  vbc: number;
  picms: number;
  vicms: number;
  predbc: number;
  modbc: string;
  modbcst?: number;
  desconto: number;
  vseg: number;
  cstpis: string;
  picmsst:number;
  ppis: number;
  vipi: number;
  pipi: number;
  cstipi: number;
  vbcipi: number;
  vpis: number;
  vbcpis: number;
  cstcofins: string;
  vbccofins: number;
  pcofins: number;
  vcofins: number;
  bcicmsst: number;
  total: number;
  indtot: number;
  // Não é coluna de itensnfe — só usado localmente pra decidir a precisão
  // do Preço Tributado (ver DialogNfe.tsx). Precisa ficar fora do payload
  // enviado ao backend (ver create/updateById mais abaixo).
  qtdglp?: number;
}
interface IItensNFe {
  iditens: string;
  idprodutos: string;
  vfrete: number;
  voutros: number;
  vicmsst: number;
  ean: string;
  descricao: string;
  ncm: string;
  origem: number;
  cfop: number | null;
  un: string;
  untrib: string;
  qtd: number;
  qtdtrib: number;
  preco: number;
  precotrib: number;
  cst: string;
  vbc: number;
  picms: number;
  vicms: number;
  predbc: number;
  modbc: string;
  modbcst?: string;
  desconto: number;
  vseg: number;
  cstpis: string;
  picmsst:number;
  ppis: number;
  vipi: number;
  pipi: number;
  cstipi: string;
  vbcipi: number;
  vpis: number;
  vbcpis: number;
  cstcofins: string;
  vbccofins: number;
  pcofins: number;
  vcofins: number;
  bcicmsst: number;
  total: number;
  indtot: number;
  // Não é coluna de itensnfe — só usado localmente pra decidir a precisão
  // do Preço Tributado (ver DialogNfe.tsx). Precisa ficar fora do payload
  // enviado ao backend (ver create/updateById mais abaixo).
  qtdglp?: number;
}
interface IItensNFe {
  iditens: string;
  idprodutos: string;
  vfrete: number;
  voutros: number;
  vicmsst: number;
  ean: string;
  descricao: string;
  ncm: string;
  origem: number;
  cfop: number | null;
  un: string;
  untrib: string;
  qtd: number;
  qtdtrib: number;
  preco: number;
  precotrib: number;
  cst: string;
  vbc: number;
  picms: number;
  vicms: number;
  predbc: number;
  modbc: string;
  modbcst?: string;
  desconto: number;
  vseg: number;
  cstpis: string;
  picmsst:number;
  ppis: number;
  vipi: number;
  pipi: number;
  cstipi: string;
  vbcipi: number;
  vpis: number;
  vbcpis: number;
  cstcofins: string;
  vbccofins: number;
  pcofins: number;
  vcofins: number;
  bcicmsst: number;
  total: number;
  indtot: number;
  filtro: string;
  // TAX do cadastro atual do produto (ver DetalheDeProduto.tsx) — quando
  // false, o item é ignorado ao gerar/enviar a nota pra SEFAZ (ver
  // GeraNfe.ts no backend) e não deveria contar no total. Só vem preenchido
  // no getById (join com produtos), não é gravado no item.
  produtotax?: boolean;
}
interface Igetnfe {
  serie: number; 
  nfe: number; 
  indpag: number; 
  natureza: string;
  dhemi: string; 
  tpemis: number; 
  nfecpl: string;
  volumequantidade: string;
  volumemarca: string;
  volesp: string;
  volumepesobruto: number;
  volumepesoliquido: number;
  numerovolume: string;
  tpag: string; 
  finnfe: number; 
  vbc: number; 
  vicms: number; 
  vprod: number;
  vfrete: number;
  vseg: number;
  vdesc: number;
  vipi: number;
  vconfins: number;
  vpis: number;
  vnfe: number;
  tpnfe: number; 
  modfrete: number;
  idparceiro: string; 
  idtransportadora?: string | null;
  idveiculo?: string | null; 
  status: number;
  itens: IItensNFe[];
}
interface IDetalhenfe {
  nfe: number; 
  indpag: number; 
  natureza: string;
  dhemi: string; 
  tpemis: number; 
  nfecpl: string;
  volumequantidade: number;
  volumemarca: string;
  volesp: string;
  volumepesobruto: number;
  volumepesoliquido: number;
  numerovolume: string;
  tpag: string; 
  finnfe: number; 
  vbc: number; 
  vicms: number; 
  vprod: number;
  vfrete: number;
  vseg: number;
  vdesc: number;
  vipi: number;
  vconfins: number;
  vpis: number;
  vnfe: number;
  tpnfe: number; 
  modfrete: number;
  idparceiro: string; 
  idtransportadora?: string | null;
  idveiculo?: string | null; 
}
interface IItensids {
  iditens: string;
}
export interface IRNFE {
  serie: number;
  nfe: number;
  dataemissao: Date;
  nome: string;
  fazenda: string;
  cpf: string;
  cfop: number;
  vnfe: number;
  finalidade: string;
  status: string;
}
interface INfecancelar {
  justificativa: string;
  nseqevento: number;
}
interface INfeCartaCorrecao {
  xtexto: string;
  nseqevento: number;
}
type TotalCount = {
  data: IListagemNfe[];
  totalCount: number;
}
type TotalCountNfece = {
  data: IListagemNfece[];
  totalCount: number;
}
interface ErrorResponse {
  errors: {
    default: string;
  };
}
export interface IHistoricoEmissao {
  idhistorico: string;
  tentativa: number;
  sucesso: boolean;
  etapa: string;
  motivo: string;
  detalhe: string | null;
  cstat: string | null;
  datahora: string;
  usuario: string | null;
  // Configuração usada na tentativa (forma de pagamento, bandeira, código de
  // autorização...), já em texto. null nas tentativas anteriores a isso.
  configuracao?: { rotulo: string; valor: string }[] | null;
}

// Todas as tentativas de emissão de uma NF-e/NFC-e (mais recente primeiro).
const getHistoricoEmissao = async (tipo: 'nfe' | 'nfce', id: string): Promise<IHistoricoEmissao[] | Error> => {
  try {
    const { data } = await Api.get<IHistoricoEmissao[]>(`/${tipo === 'nfe' ? 'nfe' : 'nfece'}/${id}/historico`);
    return data || [];
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ErrorResponse>;
      if (axiosError.response) return new Error(axiosError.response.data?.errors?.default || 'Erro ao consultar o histórico');
      if (axiosError.request) return new Error('Nenhuma resposta do servidor');
      return new Error(axiosError.message);
    }
    return new Error('Erro inesperado');
  }
};

const getAllNFCe = async (page = 1, filter = '', tipo = '', limit = Environment.LIMITE_DE_LINHAS): Promise<TotalCountNfece | Error> => {
  try {
    const urlRelativa = `/nfece?page=${page}&limit=${limit}&filter=${filter}&tipo=${tipo}`;
    const { data, headers } = await Api.get(urlRelativa);
    if (data) {
      return {
        data,
        totalCount: Number(headers['x-total-count'] || limit),
      };
    }
    return new Error('Erro ao listar os registros.');
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ErrorResponse>;
      if (axiosError.response) {
        const errorMessage = axiosError.response.data.errors?.default || 'Erro desconhecido';
        return new Error(errorMessage);
      } else if (axiosError.request) {
        return new Error('Nenhuma resposta do servidor');
      } else {
        return new Error(axiosError.message);
      }
    } else {
      return new Error('Erro inesperado');
    }
  }
};
const getAll = async (page = 1, filter = '', tipo = '', limit = Environment.LIMITE_DE_LINHAS): Promise<TotalCount | Error> => {
  try {
    const urlRelativa = `/nfe?page=${page}&limit=${limit}&filter=${filter}&tipo=${tipo}`;
    const { data, headers } = await Api.get(urlRelativa);
    if (data) {
      return {
        data,
        totalCount: Number(headers['x-total-count'] || limit),
      };
    }
    return new Error('Erro ao listar os registros.');
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ErrorResponse>;
      if (axiosError.response) {
        const errorMessage = axiosError.response.data.errors?.default || 'Erro desconhecido';
        return new Error(errorMessage);
      } else if (axiosError.request) {
        return new Error('Nenhuma resposta do servidor');
      } else {
        return new Error(axiosError.message);
      }
    } else {
      return new Error('Erro inesperado');
    }
  }
};
const getById = async (id: string): Promise<Igetnfe | Error> => {
  try {
    const { data } = await Api.get(`/nfe/${id}`);
    if (data) {
      return data;
    }
    return new Error('Erro ao consultar o registro.');
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ErrorResponse>;
      if (axiosError.response) {
        const errorMessage = axiosError.response.data.errors?.default || 'Erro desconhecido';
        return new Error(errorMessage);
      } else if (axiosError.request) {
        return new Error('Nenhuma resposta do servidor');
      } else {
        return new Error(axiosError.message);
      }
    } else {
      return new Error('Erro inesperado');
    }
  }
};
const create = async (dados: IDetalhenfe, dadositens: IItensNFe[]): Promise<string | Error> => {
  try {
    const guuid = uuidv4();
    const dadosExtraidos = dadositens.map(({ descricao, ncm, ean, origem, filtro, qtdglp, produtotax,...restoDoObjeto }) => ({ idnfe: guuid, gtin: eanParaGtin(ean), ...restoDoObjeto }));
    const { nfe, ...resto } = dados;
    const dadosnfe = {
      idnfe: guuid,
      serie: 1,
      ...resto
    };
    const { data } = await Api.post<IDetalhenfe>('/nfe', [dadosnfe, dadosExtraidos]);

    if (Object.keys(data).length === 0) {
      return guuid;
    }
    return new Error('Erro ao criar o registro.');
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ErrorResponse>;
      if (axiosError.response) {
        const errorMessage = axiosError.response.data.errors?.default || 'Erro desconhecido';
        return new Error(errorMessage);
      } else if (axiosError.request) {
        return new Error('Nenhuma resposta do servidor');
      } else {
        return new Error(axiosError.message);
      }
    } else {
      return new Error('Erro inesperado');
    }
  }
};
const updateById = async (id: string, dados: IDetalhenfe, dadositens: IItensNFe[], iddeletados: IItensids[]): Promise<void | Error> => {
  try {
     const dadoscomid: IDetalhenfe & { idnfe: string } = { idnfe: id,...dados,};
    const dadosExtraidos = dadositens.map(({ descricao, ncm ,ean,origem, filtro, qtdglp, produtotax,...restoDoObjeto }) => ({ idnfe: id, gtin: eanParaGtin(ean), ...restoDoObjeto }));
    await Api.put(`/nfe/${id}`, [dadoscomid, dadosExtraidos, iddeletados]);
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ErrorResponse>;
      if (axiosError.response) {
        const errorMessage = axiosError.response.data.errors?.default || 'Erro desconhecido';
        return new Error(errorMessage);
      } else if (axiosError.request) {
        return new Error('Nenhuma resposta do servidor');
      } else {
        return new Error(axiosError.message);
      }
    } else {
      return new Error('Erro inesperado');
    }
  }
};

// `itens` opcional — quem chama (ex.: DetalheDaNfe.tsx) pode passar a lista de
// itens da nota pra traduzirErroFiscal tentar mapear QUAL item tem o problema
// (cruza NCM/CFOP/EAN/CST citado no erro com os itens, ou o número do item
// quando a rejeição cita um).
const transmitir = async (id: string, itens?: IItemParaMapeamentoErro[]): Promise<Blob | Error> => {
  try {
    const response = await Api.put(
      `/transmitirnfe/${id}`,
      {},
      {
        responseType: 'blob',
      }
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const blob = error.response.data as Blob;
      try {
        const text = await blob.text();
        const json = JSON.parse(text);
        // "errors" (plural) é a chave que o backend sempre usa (ver GeraNfe.ts/
        // cancelarnfe.ts/CartaCorrecao.ts) — estava lendo "error" (singular), que
        // nunca bate, então toda rejeição da SEFAZ virava só "Erro desconhecido".
        const errorMessage = json?.errors?.default || 'Erro desconhecido na resposta';
        return new Error(traduzirErroFiscal(errorMessage, itens));
      } catch (parseError) {
        try {
          const fallback = await (error.response.data as Blob).text();
          return new Error(fallback || 'Erro desconhecido ao interpretar erro');
        } catch {
          return new Error('Erro desconhecido ao ler blob');
        }
      }
    }
    if (axios.isAxiosError(error) && error.request) {
      return new Error('Nenhuma resposta do servidor');
    }
    if (axios.isAxiosError(error)) {
      return new Error(error.message);
    }
    return new Error('Erro inesperado');
  }
};
const Imprimir = async (id: string): Promise<Blob | Error> => {
  try {
    const { data } = await Api.get(`/geradanf/${id}`, {
      responseType: 'blob',
    });
    return data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ErrorResponse>;
      if (axiosError.response) {
        const errorMessage = axiosError.response.data.errors?.default || 'Erro desconhecido';
        return new Error(errorMessage);
      } else if (axiosError.request) {
        return new Error('Nenhuma resposta do servidor');
      } else {
        return new Error(axiosError.message);
      }
    } else {
      return new Error('Erro inesperado');
    }
  }
};
const baixarXml = async (id: string): Promise<Blob | Error> => {
  try {
    const { data } = await Api.get(`/xmlnfe/${id}`, {
      responseType: 'blob',
    });
    return data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const blob = error.response.data as Blob;
      try {
        const text = await blob.text();
        const json = JSON.parse(text);
        const errorMessage = json?.errors?.default || 'Erro desconhecido na resposta';
        return new Error(errorMessage);
      } catch (parseError) {
        try {
          const fallback = await (error.response.data as Blob).text();
          return new Error(fallback || 'Erro desconhecido ao interpretar erro');
        } catch {
          return new Error('Erro desconhecido ao ler blob');
        }
      }
    }
    if (axios.isAxiosError(error) && error.request) {
      return new Error('Nenhuma resposta do servidor');
    }
    if (axios.isAxiosError(error)) {
      return new Error(error.message);
    }
    return new Error('Erro inesperado');
  }
};
const Imprimirnfeentrada = async (id: string): Promise<Blob | Error> => {
  try {
    const { data } = await Api.get(`/danfeentrada/${id}`, {
      responseType: 'blob',
    });
    return data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ErrorResponse>;
      if (axiosError.response) {
        const errorMessage = axiosError.response.data.errors?.default || 'Erro desconhecido';
        return new Error(errorMessage);
      } else if (axiosError.request) {
        return new Error('Nenhuma resposta do servidor');
      } else {
        return new Error(axiosError.message);
      }
    } else {
      return new Error('Erro inesperado');
    }
  }
};
const addprotocolo = async (id: string): Promise<string | Error> => {
  try {
    const { data } = await Api.put(`/addprotocolo/${id}`);
    return data.pdf;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ErrorResponse>;
      if (axiosError.response) {
        const errorMessage = axiosError.response.data.errors?.default || 'Erro desconhecido';
        return new Error(errorMessage);
      } else if (axiosError.request) {
        return new Error('Nenhuma resposta do servidor');
      } else {
        return new Error(axiosError.message);
      }
    } else {
      return new Error('Erro inesperado');
    }
  }
};
const cancelarnfe = async (id: string, dados: INfecancelar): Promise<Blob | Error> => {
  try {
    const { data } = await Api.put(`/cancelarnfe/${id}`, dados, {
      responseType: 'blob',
    });
    return data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const blob = error.response.data as Blob;
      try {
        const text = await blob.text();
        const json = JSON.parse(text);
        // "errors" (plural) é a chave que o backend sempre usa (ver GeraNfe.ts/
        // cancelarnfe.ts/CartaCorrecao.ts) — estava lendo "error" (singular), que
        // nunca bate, então toda rejeição da SEFAZ virava só "Erro desconhecido".
        const errorMessage = json?.errors?.default || 'Erro desconhecido na resposta';
        return new Error(traduzirErroFiscal(errorMessage));
      } catch (parseError) {
        const fallbackText = await blob.text();
        return new Error(fallbackText || 'Erro desconhecido ao processar a resposta');
      }
    } else if (axios.isAxiosError(error) && error.request) {
      return new Error('Nenhuma resposta do servidor');
    } else if (axios.isAxiosError(error)) {
      return new Error(error.message);
    } else {
      return new Error('Erro inesperado');
    }
  }
};
const cartacorrecao = async (id: string, dados: INfeCartaCorrecao): Promise<Blob | Error> => {
  try {
    const { data } = await Api.put(`/cartacorrecao/${id}`, dados, {
      responseType: 'blob',
    });
    return data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const blob = error.response.data as Blob;
      try {
        const text = await blob.text();
        const json = JSON.parse(text);
        // "errors" (plural) é a chave que o backend sempre usa (ver GeraNfe.ts/
        // cancelarnfe.ts/CartaCorrecao.ts) — estava lendo "error" (singular), que
        // nunca bate, então toda rejeição da SEFAZ virava só "Erro desconhecido".
        const errorMessage = json?.errors?.default || 'Erro desconhecido na resposta';
        return new Error(traduzirErroFiscal(errorMessage));
      } catch (parseError) {
        const fallbackText = await blob.text();
        return new Error(fallbackText || 'Erro desconhecido ao processar a resposta');
      }
    } else if (axios.isAxiosError(error) && error.request) {
      return new Error('Nenhuma resposta do servidor');
    } else if (axios.isAxiosError(error)) {
      return new Error(error.message);
    } else {
      return new Error('Erro inesperado');
    }
  }
};
const Relatorio = async (produtor = 0, tipo = '', dataInicial = '', dataFinal = ''): Promise<IRNFE[] | Error> => {
  try {
    const urlRelativa = `/relatorionfe?produtor=${produtor}&tipo=${tipo}&datainicial=${dataInicial}&datafinal=${dataFinal}`;
    const { data } = await Api.get(urlRelativa);
    if (data) {
      return data;
    }
    return new Error('Erro ao consultar o registro.');
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ErrorResponse>;
      if (axiosError.response) {
        const errorMessage = axiosError.response.data.errors?.default || 'Erro desconhecido';
        return new Error(errorMessage);
      } else if (axiosError.request) {
        return new Error('Nenhuma resposta do servidor');
      } else {
        return new Error(axiosError.message);
      }
    } else {
      return new Error('Erro inesperado');
    }
  }
};
const gerarPDFNFCe = async (id: string): Promise<Blob | Error> => {
  try {
    const { data } = await Api.get(`/geradanfnfce/${id}`, {
      responseType: 'blob',
    });
    return data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const blob = error.response.data as Blob;
      try {
        const text = await blob.text();
        const json = JSON.parse(text);
        // "errors" (plural) é a chave que o backend sempre usa (ver GeraNfe.ts/
        // cancelarnfe.ts/CartaCorrecao.ts) — estava lendo "error" (singular), que
        // nunca bate, então toda rejeição da SEFAZ virava só "Erro desconhecido".
        const errorMessage = json?.errors?.default || 'Erro desconhecido na resposta';
        return new Error(traduzirErroFiscal(errorMessage));
      } catch (parseError) {
        try {
          const fallback = await (error.response.data as Blob).text();
          return new Error(fallback || 'Erro desconhecido ao interpretar erro');
        } catch {
          return new Error('Erro desconhecido ao ler blob');
        }
      }
    }
    if (axios.isAxiosError(error) && error.request) {
      return new Error('Nenhuma resposta do servidor');
    }
    if (axios.isAxiosError(error)) {
      return new Error(error.message);
    }
    return new Error('Erro inesperado');
  }
};


export const NfeService = {
  getAll,
  gerarPDFNFCe,
  getAllNFCe,
  getHistoricoEmissao,
  create,
  getById,
  updateById,
  transmitir,
  cancelarnfe,
  Relatorio,
  addprotocolo,
  cartacorrecao,
  Imprimir,
  Imprimirnfeentrada,
  baixarXml
};